import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCodingRoutes } from "../../hooks/useCodingRoutes";
import AddProblemsModal from "../../components/CodingTracker/AddProblemsModal";
import useAssignmentStore from "../../store/assignmentStore";
import {
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Link,
  Snackbar,
} from "@mui/material";
import GlassCard from "../../components/Cards/GlassCard";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import CommonDataGrid from "../../components/CodingTracker/CommonDataGrid";
import {
  surfaceGradientSx, gradientButtonSx, gradientTextSx,
  gridHeaderBgSx, gridRowHoverSx, gridRowStripeSx,
  difficultyMuiColor,
  DANGER, DANGER_BG, WARNING, WARNING_BG, SUCCESS, SUCCESS_BG,
  PRIMARY, outlinedPrimaryButtonSx,
} from "../../utils/colors";
import { HourglassBottomRounded } from "@mui/icons-material";
import useBatchStore from "../../store/batchStore";
import { getDataGridGlobalSerialNumber } from "../../utils/dataGridSerialNumber";

const AssignmentDetailsPage = () => {
  const { batchId, assignmentId } = useParams();
  const navigate = useNavigate();
  const cr = useCodingRoutes();
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const {
    currentAssignment, problems, loading, error, adding, deleting,
    fetchAssignmentById, fetchProblems, bulkAddProblems, removeProblem,
  } = useAssignmentStore();
  const {currentBatch} = useBatchStore()

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });

  useEffect(() => {
    fetchAssignmentById(assignmentId);
    fetchProblems(assignmentId);
  }, [assignmentId, fetchAssignmentById, fetchProblems]);

  const handleAddProblems = async (links) => {
    try {
      const data = await bulkAddProblems(assignmentId, links);
      setShowModal(false);
      showToast(`Added ${data.inserted} problem(s)${data.skipped ? `, ${data.skipped} skipped (duplicate)` : ""}`);
    } catch (e) {
      showToast(e?.response?.data?.message || e.message, "error");
    }
  };

  const handleDelete = async (problemId) => {
    if (!window.confirm("Remove this problem from the assignment?")) return;
    try {
      await removeProblem(assignmentId, problemId);
      showToast("Problem removed");
    } catch (e) {
      showToast(e?.response?.data?.message || e.message, "error");
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : null;

  const easyCount   = problems.filter((p) => p.difficulty === "Easy").length;
  const mediumCount = problems.filter((p) => p.difficulty === "Medium").length;
  const hardCount   = problems.filter((p) => p.difficulty === "Hard").length;

  const columns = [
    {
      field: "index", headerName: "#", width: 60, sortable: false,
      renderCell: (params) => getDataGridGlobalSerialNumber(params),
    },
    {
      field: "title", headerName: "Problem Title", flex: 2,
      renderCell: (params) => (
        <Link href={params.row.link} target="_blank" rel="noreferrer" underline="hover" fontWeight={500}>
          {params.row.title || params.row.slug}
        </Link>
      ),
    },
    {
      field: "slug", headerName: "Slug", flex: 1.5,
      renderCell: (params) => (
        <Typography component="code" variant="body2"
          sx={{ fontFamily: "monospace", bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "difficulty", headerName: "Difficulty", width: 120,
      renderCell: (params) =>
        params.value ? (
          <Chip label={params.value} size="small" color={difficultyMuiColor(params.value)} />
        ) : (
          <Typography color="text.disabled">—</Typography>
        ),
    },
    {
      field: "link", headerName: "Link", width: 120, sortable: false,
      renderCell: (params) => (
        <Link href={params.value} target="_blank" rel="noreferrer" underline="hover" variant="body2">
          LeetCode ↗
        </Link>
      ),
    },
    {
      field: "actions", headerName: "Actions", width: 100, sortable: false,
      renderCell: (params) => (
        <Button size="small" color="error" disabled={deleting === params.row._id}
          onClick={() => handleDelete(params.row._id)}
          sx={{ textTransform: "none", minWidth: 40 }}>
          {deleting === params.row._id ? <CircularProgress size={16} /> : <DeleteRoundedIcon fontSize="small" />}
        </Button>
      ),
    },
  ];

  if (loading)
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (error)
    return <Box p={4}><Alert severity="error">{error}</Alert></Box>;


  return (
    <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1200, mx: "auto", minHeight: "100vh" }}>
      <Snackbar open={toast.open} autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>

      

      <AppBreadcrumbs items={[
        { label: "Coding Tracker", path: cr.trackerHome },
        { label: currentBatch?.name || 'Batch', path: cr.batch(batchId) },
        { label: "Assignments", path: cr.assignments(batchId) },
        { label: currentAssignment?.title || "Assignment" },
      ]} />

      <GlassCard hoverable={false} sx={{
        p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 4, ...surfaceGradientSx,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ ...gradientTextSx, mb: 1.5 }}>
            {currentAssignment?.title}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {currentAssignment?.dueDate && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarTodayRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">Due: {formatDate(currentAssignment.dueDate)}</Typography>
              </Stack>
            )}
            {currentAssignment?.description && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <NotesRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">{currentAssignment.description}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => setShowModal(true)} sx={{ ...gradientButtonSx, px: 3, py: 1.2 }}>
            Add Problems
          </Button>
        
          <Button size="small"
            startIcon={<HourglassBottomRounded />}
            onClick={() => navigate(cr.assignmentProgress(batchId, assignmentId))}
            sx={{ ...outlinedPrimaryButtonSx, px: 3, py: 1.2 }}>
            View Progress
          </Button>
        </Stack>
      </GlassCard>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap" }}>
        {[
          { label: `${problems.length} Total`, color: PRIMARY, bg: "#ede9fe" },
          { label: `${easyCount} Easy`, color: SUCCESS, bg: SUCCESS_BG },
          { label: `${mediumCount} Medium`, color: WARNING, bg: WARNING_BG },
          { label: `${hardCount} Hard`, color: DANGER, bg: DANGER_BG },
        ].map(({ label, color, bg }) => (
          <Chip key={label} label={label} sx={{ fontWeight: 700, fontSize: "0.8rem", color, bgcolor: bg, border: "none", px: 0.5 }} />
        ))}
      </Stack>

      {problems.length === 0 ? (
        <GlassCard hoverable={false} sx={{ textAlign: "center", py: 10, border: "2px dashed", borderColor: "divider", borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary" mb={1}>No problems yet</Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>Start by adding LeetCode problems to this assignment.</Typography>
          <Button variant="contained" startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => setShowModal(true)} sx={{ ...gradientButtonSx, px: 3 }}>
            Add First Problems
          </Button>
        </GlassCard>
      ) : (
        <GlassCard hoverable={false} sx={{ borderRadius: 4, overflow: "hidden" }}>
          <CommonDataGrid
            firstColumnField="index"
            rows={problems.map((p) => ({ ...p, id: p._id }))}
            columns={columns}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                ...gridHeaderBgSx,
                fontWeight: 700,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDataGrid-row": gridRowHoverSx,
              ...gridRowStripeSx,
              "& .MuiDataGrid-cell": { py: 1, alignItems: "center" },
              "& .MuiDataGrid-virtualScroller": { minHeight: 200 },
            }}
          />
        </GlassCard>
      )}

      {showModal && (
        <AddProblemsModal onClose={() => setShowModal(false)} onSubmit={handleAddProblems} loading={adding} />
      )}
    </Box>
  );
};

export default AssignmentDetailsPage;
